import React, { useState, useEffect, useCallback } from 'react';
import { jobDescriptionText } from '../utils/jobDescription';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, X, RefreshCw, ChevronLeft, ChevronRight,
    Car, User, Wrench, MessageSquare, ArrowLeft,
    CalendarDays, Filter, Package, MapPin, Users, Hash,
    AlertTriangle, Clock, CheckCircle
} from 'lucide-react';
import { TableProperties } from 'lucide-react';
import Layout from './Layout';
import {
    fetchJob,
    selectJob,
    clearSelectedJob,
    openViewModal,
    closeViewModal,
} from '../store/slices/jobSlice';
import axiosInstance from '../services/axiosInstance';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const NOTES_PER_PAGE = 5;

const ALL_STATUSES = [
    { value: '',                  label: 'All Statuses' },
    { value: 'CREATED',           label: 'Created' },
    { value: 'QUOTED',            label: 'Quoted' },
    { value: 'APPROVED',          label: 'Approved' },
    { value: 'IN_PROGRESS',       label: 'In Progress' },
    { value: 'PAUSED',            label: 'Paused' },
    { value: 'COMPLETED',         label: 'Completed' },
    { value: 'INVOICED',          label: 'Invoiced' },
    { value: 'CANCELLED',         label: 'Cancelled' },
    { value: 'VEHICLE_COLLECTED', label: 'Vehicle Collected' },
];

const STATUS_COLORS = {
    CREATED:           'bg-gray-100 text-gray-700',
    QUOTED:            'bg-blue-100 text-blue-700',
    APPROVED:          'bg-indigo-100 text-indigo-700',
    IN_PROGRESS:       'bg-violet-100 text-violet-700',
    PAUSED:            'bg-amber-100 text-amber-700',
    COMPLETED:         'bg-emerald-100 text-emerald-700',
    INVOICED:          'bg-teal-100 text-teal-700',
    CANCELLED:         'bg-red-100 text-red-700',
    VEHICLE_COLLECTED: 'bg-sky-100 text-sky-700',
};

const PRIORITY_COLORS = {
    URGENT: 'bg-red-100 text-red-700',
    HIGH:   'bg-orange-100 text-orange-700',
    NORMAL: 'bg-gray-100 text-gray-600',
    LOW:    'bg-blue-100 text-blue-600',
};

const NOTE_TYPE_LABEL = {
    GENERAL:                'General',
    INSPECTION:             'Inspection',
    PROGRESS_UPDATE:        'Progress',
    CUSTOMER_COMMUNICATION: 'Customer',
    PARTS_ORDER:            'Parts',
    HANDOVER:               'Handover',
};

const fmt = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const THRESHOLD_STYLES = {
    green:        { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Good' },
    amber:        { bg: 'bg-amber-50',   border: 'border-amber-300',   text: 'text-amber-700',   dot: 'bg-amber-400',  label: 'Monitor' },
    red:          { bg: 'bg-red-50',     border: 'border-red-300',     text: 'text-red-700',     dot: 'bg-red-500',    label: 'Action required' },
    unclassified: { bg: 'bg-white',      border: 'border-gray-200',    text: 'text-gray-400',    dot: 'bg-gray-300',   label: '' },
};

// ── Read-only Job Detail Modal ─────────────────────────────────────────────────

const JobDetailModal = ({ job, onClose }) => {
    const [notesPage, setNotesPage] = useState(0);

    if (!job) return null;

    const notes = [...(job.notes || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const totalNotePages = Math.ceil(notes.length / NOTES_PER_PAGE);
    const pagedNotes = notes.slice(notesPage * NOTES_PER_PAGE, (notesPage + 1) * NOTES_PER_PAGE);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">

                {/* Header */}
                <div className="px-6 py-4 border-b flex items-start justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center shrink-0">
                            <Wrench size={18} className="text-violet-600" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-xl font-bold text-gray-900">{job.jobNumber}</h2>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}>
                                    {job.status?.replace(/_/g, ' ')}
                                </span>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_COLORS[job.priority] || ''}`}>
                                    {job.priority}
                                </span>
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                    Read only
                                </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {job.customerName} · {job.vehicleDescription || job.vehicleRego}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl shrink-0">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

                    {/* Info grid */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs text-gray-400 mb-1">Customer</p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{job.customerName}</p>
                            {job.customerPhone && <p className="text-xs text-gray-500">{job.customerPhone}</p>}
                            {job.customerEmail && <p className="text-xs text-gray-500 truncate">{job.customerEmail}</p>}
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs text-gray-400 mb-1">Vehicle</p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{job.vehicleDescription}</p>
                            {job.vehicleRego && <p className="text-xs text-gray-500">Rego: {job.vehicleRego}</p>}
                            {job.vehicleMake && <p className="text-xs text-gray-500">{job.vehicleMake} {job.vehicleModel} {job.vehicleYear}</p>}
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs text-gray-400 mb-1">Assigned To</p>
                            {job.assignedStaffName
                                ? <p className="text-sm font-semibold text-violet-700 truncate">{job.assignedStaffName}</p>
                                : <p className="text-sm text-gray-400 italic">Unassigned</p>
                            }
                        </div>
                        {job.scheduledStartDate && (
                            <div className="bg-gray-50 rounded-xl p-3">
                                <p className="text-xs text-gray-400 mb-1">Scheduled</p>
                                <p className="text-sm font-semibold text-gray-900">{fmtDate(job.scheduledStartDate)}</p>
                                {job.scheduledEndDate && <p className="text-xs text-gray-500">to {fmtDate(job.scheduledEndDate)}</p>}
                            </div>
                        )}
                        {job.actualStartDate && (
                            <div className="bg-gray-50 rounded-xl p-3">
                                <p className="text-xs text-gray-400 mb-1">Actual Start</p>
                                <p className="text-sm font-semibold text-gray-900">{fmtDate(job.actualStartDate)}</p>
                                {job.actualEndDate && <p className="text-xs text-gray-500">Ended {fmtDate(job.actualEndDate)}</p>}
                            </div>
                        )}
                        {job.odometerIn != null && (
                            <div className="bg-gray-50 rounded-xl p-3">
                                <p className="text-xs text-gray-400 mb-1">Odometer In</p>
                                <p className="text-sm font-semibold text-gray-900">{job.odometerIn.toLocaleString()} km</p>
                                {job.odometerOut != null && <p className="text-xs text-gray-500">Out: {job.odometerOut.toLocaleString()} km</p>}
                            </div>
                        )}
                        {job.totalEstimatedCost > 0 && (
                            <div className="bg-gray-50 rounded-xl p-3">
                                <p className="text-xs text-gray-400 mb-1">Est. Cost</p>
                                <p className="text-sm font-semibold text-gray-900">${Number(job.totalEstimatedCost).toFixed(2)}</p>
                            </div>
                        )}
                        <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs text-gray-400 mb-1">Last Updated</p>
                            <p className="text-sm font-semibold text-gray-900">{fmt(job.updatedAt)}</p>
                            <p className="text-xs text-gray-500">Created {fmt(job.createdAt)}</p>
                        </div>
                    </div>

                    {/* Description */}
                    {/* ⚠️ Via the helper — job.description is a structured object and
                        rendering it directly throws, blanking the page. */}
                    {jobDescriptionText(job.description) && (
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Description</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3 leading-relaxed">
                                {jobDescriptionText(job.description)}
                            </p>
                        </div>
                    )}

                    {/* Inspection notes */}
                    {job.inspectionNotes && (
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Inspection Notes</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3 leading-relaxed whitespace-pre-wrap">{job.inspectionNotes}</p>
                        </div>
                    )}

                    {/* Work Items — read only */}
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Work Items {job.workItems?.length > 0 && `(${job.workItems.length})`}
                        </p>
                        {(!job.workItems || job.workItems.length === 0) ? (
                            <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                                <Wrench size={20} className="mx-auto mb-2 opacity-40" />
                                No work items
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {job.workItems.map(item => {
                                    const labourVal = Number(item.costDetail?.labour?.value) || 0;
                                    const partsVal  = (item.costDetail?.parts ?? []).reduce((s, p) => s + (Number(p.value) || 0), 0);
                                    const total = labourVal + partsVal;
                                    return (
                                        <div key={item.uuid} className="bg-gray-50 rounded-xl px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{item.description}</p>
                                                    {item.detailNotes && <p className="text-xs text-gray-500 mt-0.5">{item.detailNotes}</p>}
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {total > 0 && (
                                                        <span className="text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                                                            ${total.toFixed(2)}
                                                        </span>
                                                    )}
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        item.status === 'COMPLETED'             ? 'bg-emerald-100 text-emerald-700' :
                                                            item.status === 'IN_PROGRESS'           ? 'bg-blue-100 text-blue-700' :
                                                                item.status === 'AWAITING_CONFIRMATION' ? 'bg-amber-100 text-amber-700' :
                                                                    item.status === 'DECLINED'              ? 'bg-red-100 text-red-700' :
                                                                        item.status === 'CONFIRMED'             ? 'bg-sky-100 text-sky-700' :
                                                                            'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {item.status?.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Cost breakdown */}
                                            {item.costDetail && (labourVal > 0 || partsVal > 0) && (
                                                <div className="mt-2 pt-2 border-t border-gray-200 flex flex-wrap gap-3">
                                                    {labourVal > 0 && (
                                                        <span className="text-xs text-gray-500">
                                                            Labour: <span className="font-medium text-gray-700">${labourVal.toFixed(2)}</span>
                                                            {item.costDetail.labour?.description && ` · ${item.costDetail.labour.description}`}
                                                        </span>
                                                    )}
                                                    {(item.costDetail.parts ?? []).map((p, i) => (
                                                        <span key={i} className="text-xs text-gray-500">
                                                            {p.partNo && <span className="font-mono text-gray-400 mr-1">{p.partNo}</span>}
                                                            ${Number(p.value || 0).toFixed(2)}
                                                            {p.description && ` · ${p.description}`}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Measurements — read only */}
                                            {item.measurementObservations?.length > 0 && (
                                                <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle size={12} className="text-violet-500 shrink-0" />
                                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-1">Measurements</p>
                                                        {item.measurementsComplete
                                                            ? <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle size={11} /> Complete</span>
                                                            : <span className="text-xs text-amber-500 font-semibold flex items-center gap-1"><AlertTriangle size={11} /> Incomplete</span>
                                                        }
                                                    </div>
                                                    {item.measurementObservations.map(obs => {
                                                        const band = (obs.observationStatus || '').toLowerCase();
                                                        const style = THRESHOLD_STYLES[band] || THRESHOLD_STYLES.unclassified;
                                                        return (
                                                            <div key={obs.measurementUuid} className={`rounded-lg border p-3 ${style.bg} ${style.border}`}>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                                                    <span className="text-xs font-semibold text-gray-700 flex-1">{obs.name}</span>
                                                                    {obs.unitSymbol && <span className="text-xs text-gray-400 font-mono shrink-0">{obs.unitSymbol}</span>}
                                                                    {band && band !== 'unclassified' && <span className={`text-xs font-bold shrink-0 ${style.text}`}>{style.label}</span>}
                                                                </div>
                                                                <div className={`px-3 py-1.5 rounded-lg text-sm font-mono border ${style.bg} ${style.border}`}>
                                                                    {obs.value || <span className="text-gray-400 italic">No value recorded</span>}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Notes — read only */}
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Notes {notes.length > 0 && `(${notes.length})`}
                        </p>
                        {notes.length === 0 ? (
                            <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                                <MessageSquare size={20} className="mx-auto mb-2 opacity-40" />
                                No notes
                            </div>
                        ) : (
                            <>
                                <div className="space-y-3">
                                    {pagedNotes.map(note => {
                                        const initials = note.authorName
                                            ? note.authorName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                            : '?';
                                        return (
                                            <div key={note.uuid} className="flex gap-3 bg-gray-50 rounded-xl p-4">
                                                <div className="w-8 h-8 rounded-full bg-violet-200 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">
                                                    {initials}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className="text-sm font-semibold text-gray-900">{note.authorName}</span>
                                                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">
                                                            {NOTE_TYPE_LABEL[note.noteType] || note.noteType}
                                                        </span>
                                                        {note.customerVisible && (
                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Customer visible</span>
                                                        )}
                                                        <span className="text-xs text-gray-400 ml-auto">{fmt(note.createdAt)}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {totalNotePages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                                        <button
                                            onClick={() => setNotesPage(p => Math.max(0, p - 1))}
                                            disabled={notesPage === 0}
                                            className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-40"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-xs text-gray-500">{notesPage + 1} / {totalNotePages}</span>
                                        <button
                                            onClick={() => setNotesPage(p => Math.min(totalNotePages - 1, p + 1))}
                                            disabled={notesPage >= totalNotePages - 1}
                                            className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-40"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t bg-gray-50 rounded-b-2xl shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border rounded-xl hover:bg-gray-100 text-gray-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main Page ──────────────────────────────────────────────────────────────────

const JobsSearchPage = ({ onBack }) => {
    const dispatch = useDispatch();

    // Search / filter state — all local (not in Redux, avoids polluting Kanban state)
    const [query, setQuery]             = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFrom, setDateFrom]       = useState('');
    const [dateTo, setDateTo]           = useState('');
    const [page, setPage]               = useState(0);

    // Data state
    const [jobs, setJobs]               = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages]   = useState(0);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);

    // Detail modal state
    const [selectedJob, setSelectedJob]   = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            params.set('page', page);
            params.set('size', PAGE_SIZE);
            params.set('sort', 'updatedAt,desc');

            // Use search endpoint when query present, otherwise general listing
            // with optional status filter
            if (query.trim()) {
                params.set('q', query.trim());
                const res = await axiosInstance.get(`/jobs/search?${params}`);
                const data = res.data;
                setJobs(data.content ?? data);
                setTotalElements(data.totalSize ?? data.totalElements ?? (data.content ?? data).length);
                setTotalPages(data.totalPages ?? Math.ceil((data.totalSize ?? (data.content ?? data).length) / PAGE_SIZE));
            } else if (statusFilter) {
                // Status filter — use /status/:status (no pagination on this endpoint, slice client-side)
                const res = await axiosInstance.get(`/jobs/status/${statusFilter}`);
                const all = res.data ?? [];
                // Apply date range filter and sort descending by updatedAt client-side
                const filtered = applyDateFilter(all)
                    .sort((a, b) => new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0));
                const start = page * PAGE_SIZE;
                setJobs(filtered.slice(start, start + PAGE_SIZE));
                setTotalElements(filtered.length);
                setTotalPages(Math.ceil(filtered.length / PAGE_SIZE));
            } else {
                // General paginated listing
                const res = await axiosInstance.get(`/jobs?${params}`);
                const data = res.data;
                let rows = data.content ?? data ?? [];
                // Apply date filter client-side when paginated endpoint doesn't support it
                if (dateFrom || dateTo) {
                    rows = applyDateFilter(rows);
                }
                setJobs(rows);
                setTotalElements(data.totalSize ?? data.totalElements ?? rows.length);
                setTotalPages(data.totalPages ?? Math.ceil((data.totalSize ?? rows.length) / PAGE_SIZE));
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load jobs');
        } finally {
            setLoading(false);
        }
    }, [query, statusFilter, dateFrom, dateTo, page]);

    const applyDateFilter = (rows) => {
        return rows.filter(j => {
            const updatedAt = j.updatedAt ? new Date(j.updatedAt) : null;
            if (!updatedAt) return true;
            if (dateFrom && updatedAt < new Date(dateFrom)) return false;
            if (dateTo   && updatedAt > new Date(dateTo + 'T23:59:59')) return false;
            return true;
        });
    };

    useEffect(() => { fetchJobs(); }, [fetchJobs]);

    // Reset to page 0 when filters change
    const handleFilterChange = (setter) => (val) => {
        setter(val);
        setPage(0);
    };

    // ── Row click — load full detail ───────────────────────────────────────────

    const handleRowClick = async (job) => {
        setDetailLoading(true);
        try {
            const res = await axiosInstance.get(`/jobs/${job.uuid}`);
            setSelectedJob(res.data);
        } catch (e) {
            // Fall back to summary data if detail fetch fails
            setSelectedJob(job);
        } finally {
            setDetailLoading(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    const totalPagesDisplay = Math.max(1, totalPages);

    return (
        <Layout>
            <div className="p-4">

                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center">
                            <button
                                onClick={onBack}
                                className="w-10 h-10 bg-gray-100 rounded-l-xl flex items-center justify-center hover:bg-gray-200 transition-colors"
                                title="Back to Jobs Board"
                            >
                                <ArrowLeft size={16} className="text-gray-600" />
                            </button>
                            <div className="w-10 h-10 bg-violet-50 rounded-r-xl flex items-center justify-center border-l border-violet-100">
                                <TableProperties size={18} className="text-violet-600" />
                            </div>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Job History</h1>
                            <p className="text-sm text-gray-500">
                                {totalElements > 0 ? `${totalElements} job${totalElements !== 1 ? 's' : ''}` : 'All workshop jobs'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => fetchJobs()}
                        disabled={loading}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Filter bar */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    {/* Text search */}
                    <div className="relative flex-1 min-w-48">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by job number..."
                            value={query}
                            onChange={e => handleFilterChange(setQuery)(e.target.value)}
                            className="w-full pl-9 pr-9 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                        {query && (
                            <button onClick={() => handleFilterChange(setQuery)('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Status filter */}
                    <div className="relative">
                        <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select
                            value={statusFilter}
                            onChange={e => handleFilterChange(setStatusFilter)(e.target.value)}
                            className="pl-8 pr-8 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white appearance-none cursor-pointer"
                        >
                            {ALL_STATUSES.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date from */}
                    <div className="relative">
                        <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => handleFilterChange(setDateFrom)(e.target.value)}
                            className="pl-8 pr-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            title="Updated from"
                        />
                    </div>
                    <span className="text-gray-400 text-sm">to</span>
                    {/* Date to */}
                    <div className="relative">
                        <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="date"
                            value={dateTo}
                            onChange={e => handleFilterChange(setDateTo)(e.target.value)}
                            className="pl-8 pr-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            title="Updated to"
                        />
                    </div>

                    {/* Clear filters */}
                    {(query || statusFilter || dateFrom || dateTo) && (
                        <button
                            onClick={() => {
                                setQuery(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(0);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 border border-dashed rounded-xl hover:bg-gray-50"
                        >
                            <X size={13} /> Clear
                        </button>
                    )}
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl border overflow-hidden">
                    {/* Loading overlay */}
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-violet-600 gap-2 text-sm">
                            <RefreshCw size={16} className="animate-spin" /> Loading jobs…
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="flex items-center gap-2 m-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}

                    {/* Table content */}
                    {!loading && !error && (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Job #</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Vehicle</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Priority</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Assigned</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Est. Cost</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Last Updated ↓</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                    {jobs.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-16 text-center text-gray-400">
                                                <Search size={24} className="mx-auto mb-2 opacity-30" />
                                                No jobs found
                                            </td>
                                        </tr>
                                    ) : (
                                        jobs.map(job => (
                                            <tr
                                                key={job.uuid}
                                                onClick={() => handleRowClick(job)}
                                                className="hover:bg-violet-50 cursor-pointer transition-colors"
                                            >
                                                <td className="px-4 py-3 font-mono text-xs font-medium text-gray-900 whitespace-nowrap">
                                                    {job.jobNumber}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-gray-900 truncate max-w-[160px]">{job.customerName}</p>
                                                    {job.customerPhone && <p className="text-xs text-gray-400">{job.customerPhone}</p>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-gray-800 truncate max-w-[180px]">{job.vehicleDescription || '—'}</p>
                                                    {job.vehicleRego && (
                                                        <p className="text-xs text-gray-400 font-mono">{job.vehicleRego}</p>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}>
                                                            {job.status?.replace(/_/g, ' ')}
                                                        </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_COLORS[job.priority] || 'bg-gray-100 text-gray-600'}`}>
                                                            {job.priority}
                                                        </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1.5">
                                                        <User size={12} className="text-gray-400 shrink-0" />
                                                        <span className="text-gray-700 truncate max-w-[120px]">
                                                                {job.assignedStaffName || <span className="text-gray-400 italic">Unassigned</span>}
                                                            </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                                                    {job.totalEstimatedCost > 0
                                                        ? `$${Number(job.totalEstimatedCost).toFixed(2)}`
                                                        : <span className="text-gray-400">—</span>
                                                    }
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                                    {fmt(job.updatedAt)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                                <p className="text-xs text-gray-500">
                                    {jobs.length > 0
                                        ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalElements)} of ${totalElements}`
                                        : 'No results'
                                    }
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={page === 0 || loading}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
                                    >
                                        <ChevronLeft size={14} /> Prev
                                    </button>
                                    <span className="text-xs text-gray-600 px-1">
                                        Page {page + 1} of {totalPagesDisplay}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPagesDisplay - 1, p + 1))}
                                        disabled={page >= totalPagesDisplay - 1 || loading}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
                                    >
                                        Next <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Detail modal */}
                {detailLoading && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl px-8 py-6 flex items-center gap-3 shadow-xl text-violet-600">
                            <RefreshCw size={18} className="animate-spin" />
                            <span className="text-sm font-medium">Loading job details…</span>
                        </div>
                    </div>
                )}

                {selectedJob && !detailLoading && (
                    <JobDetailModal
                        job={selectedJob}
                        onClose={() => setSelectedJob(null)}
                    />
                )}
            </div>
        </Layout>
    );
};

export default JobsSearchPage;